import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle } from "lucide-react";

export default function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title,
  description,
  confirmLabel = "Confirm",
  variant = "default",
  // SOUND-618: optional body content (a reason field, a suppression toggle, …)
  // rendered between the description and the buttons. All 16 pre-existing
  // callers are self-closing, so this is additive — they render unchanged.
  children,
  // Lets a caller gate Confirm on its own validation (e.g. a required reason).
  confirmDisabled = false,
  // "sm" keeps the original width; "md" gives body content room to breathe.
  size = "sm",
}) {
  const cancelRef = useRef(null);

  useEffect(() => {
    if (open) cancelRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "Escape") onCancel();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [open, onCancel]);

  if (!open) return null;

  const isDestructive = variant === "destructive";

  // SOUND-239: render via portal so the overlay escapes any parent stacking
  // context (notably the topbar wrapper at z-20 that otherwise traps fixed
  // descendants of <main>).
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onCancel}
    >
      <div
        className={`surface rounded-xl ring-1 ring-border p-6 w-full shadow-lg ${
          size === "md" ? "max-w-md" : "max-w-sm"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          {isDestructive && (
            <div className="p-2 rounded-full bg-destructive/10 shrink-0">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold">{title}</h3>
            {description && (
              <p className="text-sm text-muted-foreground mt-1">
                {description}
              </p>
            )}
          </div>
        </div>
        {children && <div className="mb-4 space-y-3">{children}</div>}
        <div className="flex gap-2 justify-end">
          <button
            ref={cancelRef}
            className="btn btn-ghost"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className={`btn ${isDestructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : "btn-primary"}`}
            onClick={onConfirm}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
